import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { DotsThree } from "@phosphor-icons/react";
import { placeRowMenu } from "./rowMenuPlacement";

/**
 * The actions that belong to one thing, behind one small button at the end of its row.
 *
 * Written here rather than taken from Radix, which the portal already carries: the Radix menu opens
 * on `pointerdown`, which jsdom does not dispatch from a click, and every write on these screens is
 * proven by a component test that presses the control the way a person does. This one opens on a
 * click, closes on Escape, on a choice and on a click anywhere else, moves with the arrow keys, and
 * returns focus to its button when it closes.
 *
 * **The list is rendered through a portal and placed by measurement, since 2026-10-07 (build round
 * 16).** It used to be absolutely positioned inside the row, so it lived inside whatever box held the
 * row. `.table-wrap` scrolls sideways with `overflow-x: auto`, and a box that scrolls one way clips
 * and scrolls the other way too, so the menu on the last people on Admin › a client › Personer och
 * åtkomst opened underneath the table's edge and Mattej had to scroll inside the panel to reach it
 * (found in production the same day). Raising a `z-index` cannot help: a scroll container clips its
 * content whatever the stacking order. So the list leaves the row: it is rendered into
 * `document.body`, `position: fixed`, under the button and aligned to its right edge, above the
 * button when there is no room below, and kept inside the viewport. Inside a modal `<dialog>` (the
 * portal's `Sheet`) it is rendered into the dialog instead, because a modal dialog makes everything
 * outside it inert and sits in the top layer above it.
 *
 * The position is set through the CSSOM, which the portal's CSP allows; only inline `style`
 * attributes and injected `<style>` elements are refused. It follows the button while anything
 * scrolls or the window is resized, and the list is checked against where it actually landed, so an
 * ancestor that creates its own containing block for fixed elements cannot move it off the button.
 */
export interface RowMenuItem {
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  testId?: string;
}

export type RowMenuEntry = RowMenuItem | "separator";

export function RowMenu({
  label,
  items,
  testId,
  trigger: triggerContent,
  triggerClassName = "icon-button is-small"
}: {
  /** What the button is for, said to a screen reader: "Åtgärder för Nordvik Bygg". */
  label: string;
  items: RowMenuEntry[];
  testId?: string;
  /** What the button shows. Three dots when left out; the account menu shows initials. */
  trigger?: ReactNode;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);

  /** The modal dialog the button is in, or the document's body. Read once per opening. */
  const [host, setHost] = useState<HTMLElement | null>(null);

  const place = useCallback(() => {
    const button = trigger.current;
    const menu = list.current;
    if (!button || !menu) {
      return;
    }

    const wanted = placeRowMenu(
      button.getBoundingClientRect(),
      { width: menu.offsetWidth, height: menu.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight }
    );

    menu.style.top = `${wanted.top}px`;
    menu.style.left = `${wanted.left}px`;

    // A transformed or filtered ancestor makes itself the containing block of a fixed element, so
    // the list would land offset by that ancestor's position. Measure where it went and correct.
    const landed = menu.getBoundingClientRect();
    if (landed.width > 0 && (landed.top !== wanted.top || landed.left !== wanted.left)) {
      menu.style.top = `${2 * wanted.top - landed.top}px`;
      menu.style.left = `${2 * wanted.left - landed.left}px`;
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    place();
  }, [open, host, place]);

  useEffect(() => {
    if (!open) {
      return;
    }

    list.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (root.current?.contains(target) || list.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", onPointer);
    // Capture, so a scroll inside any panel moves the list with its button and not only the page's.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  function close() {
    setOpen(false);
    trigger.current?.focus();
  }

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setHost(trigger.current?.closest("dialog") ?? document.body);
    setOpen(true);
  }

  function onKeyDown(event: KeyboardEvent<HTMLUListElement>) {
    const all = Array.from(
      list.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []
    );
    const index = all.indexOf(document.activeElement as HTMLButtonElement);

    if (event.key === "Escape") {
      event.preventDefault();
      close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      all[(index + 1) % all.length]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      all[(index - 1 + all.length) % all.length]?.focus();
    } else if (event.key === "Tab") {
      // The list is no longer next to its button in the document, so Tab would leave it for the end
      // of the page. Focus goes back to the button first and the browser's own Tab then moves on
      // from there, which is where it went when the list sat beside the button.
      close();
    }
  }

  const menu =
    open && host
      ? createPortal(
          <ul
            ref={list}
            className="row-menu-list"
            role="menu"
            id={menuId}
            aria-label={label}
            onKeyDown={onKeyDown}
          >
            {items.map((item, index) =>
              item === "separator" ? (
                <li key={`separator-${index}`} role="none">
                  <hr />
                </li>
              ) : (
                <li key={item.label} role="none">
                  <button
                    type="button"
                    role="menuitem"
                    className={item.danger ? "is-danger" : undefined}
                    disabled={item.disabled}
                    data-testid={item.testId}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect();
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </li>
              )
            )}
          </ul>,
          host
        )
      : null;

  return (
    <div className="row-menu" ref={root} data-testid={testId}>
      <button
        ref={trigger}
        className={triggerClassName}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={toggle}
      >
        {triggerContent ?? <DotsThree size={18} weight="bold" aria-hidden="true" />}
      </button>
      {menu}
    </div>
  );
}
